/**
 * Overview (TASK-014 Step B1) — Panoramica, the course landing page.
 *
 * Renders `Reader::OverviewQuery`'s contract (see ./types.ts `OverviewProp`
 * and the Ruby service's doc for the exact field-by-field rules): a
 * narrative greeting, a 3-indicator "polso" row (Lettura/Padronanza/
 * Verifica), a "Continua da qui" + "Da rivedere" pair, a "Mappa del corso"
 * chapter-tile grid, recent tutor conversations, and — per the task's closed
 * decision "barra tutor SOLO in Panoramica" — a fixed input bar at the
 * bottom of this page's own content (not a global fixture: it lives inside
 * this page's own flex column, so it never fights the mobile bottom tab bar
 * CourseLayout already renders).
 *
 * Style: only design-system tokens/components (Heading/Text/Card/Button/
 * Input) + the CSS custom properties every other reader/* page already uses
 * (var(--space-*), var(--ink*), var(--line), var(--accent)) — no giant
 * numbers, streaks, or emoji (task's explicit style constraint).
 *
 * Tutor draft handoff (coordination with Step B2's Tutor.tsx): submitting
 * the bar POSTs to Tutor::ConversationsController#create
 * (createTutorConversation, same endpoint the contextual drawer already
 * uses), then navigates to
 * `tutorConversationUrl(versionId, id)?draft=<encodeURIComponent(text)>`.
 * Tutor.tsx reads that query param on mount and sends it as the first
 * message — this page never talks to the streaming endpoint itself.
 */
import { useCallback, useState } from 'react'
import type { CSSProperties, FormEvent } from 'react'
import { Link, router } from '@inertiajs/react'
import Heading from '@arianna/design-system/Heading'
import Text from '@arianna/design-system/Text'
import Card from '@arianna/design-system/Card'
import Button from '@arianna/design-system/Button'
import Input from '@arianna/design-system/Input'
import CourseLayout from './CourseLayout.js'
import { createTutorConversation } from './tutorApi.js'
import { sectionUrl, letturaEntryUrl, quizHubUrl, tutorConversationUrl } from './urls.js'
import type { PageComponent } from './inertiaPage.js'
import type { OverviewPageProps, OverviewChapterProp } from './types.js'

function greetingPrefix(hour: number): string {
  if (hour < 6) return 'Buonanotte'
  if (hour < 12) return 'Buongiorno'
  if (hour < 18) return 'Buon pomeriggio'
  return 'Buonasera'
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })
}

function ratioOf(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0
}

// ---------------------------------------------------------------------------
// MicroBar — a 2px progress rail (task spec: "micro progress-bar 2px").
// The design system's Meter component bottoms out at 6px (`sm`), too tall
// for the polso row / chapter tiles here — a bespoke bar, same tokens.
// ---------------------------------------------------------------------------
function MicroBar({ ratio, testId }: { ratio: number; testId?: string }) {
  const percent = Math.round(Math.max(0, Math.min(1, ratio)) * 100)
  return (
    <div
      data-testid={testId}
      role="meter"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{ height: 2, borderRadius: 1, background: 'var(--line)', overflow: 'hidden' }}
    >
      <div
        style={{
          height: '100%',
          width: `${percent}%`,
          background: 'var(--accent)',
          borderRadius: 1,
        }}
      />
    </div>
  )
}

interface PulseItemProps {
  testId: string
  label: string
  primary: string
  secondary: string
  ratio: number
  last?: boolean
}

function PulseItem({ testId, label, primary, secondary, ratio, last }: PulseItemProps) {
  return (
    <div
      data-testid={testId}
      style={{
        flex: 1,
        minWidth: 0,
        padding: 'var(--space-4) var(--space-5)',
        borderRight: last ? undefined : '1px solid var(--line)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--ink-4)',
        }}
      >
        {label}
      </span>
      <Text variant="reader" style={{ fontSize: 16, color: 'var(--ink)' }}>
        {primary}
      </Text>
      <MicroBar ratio={ratio} />
      <Text variant="meta">{secondary}</Text>
    </div>
  )
}

function ChapterTile({
  chapter,
  index,
  manualVersionId,
  isCurrent,
  resumeSlug,
}: {
  chapter: OverviewChapterProp
  index: number
  manualVersionId: number
  isCurrent: boolean
  resumeSlug: string | null
}) {
  const targetSlug = isCurrent && resumeSlug != null ? resumeSlug : chapter.firstSlug
  const ratio = ratioOf(chapter.completedCount, chapter.totalCount)

  const content = (
    <>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-4)' }}>
        {String(index + 1).padStart(2, '0')}
      </span>
      <Text variant="ui" style={{ display: 'block', marginTop: 4 }}>
        {chapter.title}
      </Text>
      <Text variant="meta">
        {chapter.completedCount}/{chapter.totalCount}
      </Text>
      <div style={{ marginTop: 'var(--space-2)' }}>
        <MicroBar ratio={ratio} />
      </div>
    </>
  )

  const style: CSSProperties = {
    display: 'block',
    textDecoration: 'none',
    borderRadius: 10,
    padding: 'var(--space-3)',
    border: isCurrent ? '2px solid var(--accent)' : '1px solid var(--line)',
    background: 'var(--surface)',
  }

  if (targetSlug == null) {
    return (
      <div data-testid="overview-chapter-tile" style={style}>
        {content}
      </div>
    )
  }

  return (
    <Link
      data-testid="overview-chapter-tile"
      href={sectionUrl(manualVersionId, targetSlug)}
      style={style}
    >
      {content}
    </Link>
  )
}

const Overview: PageComponent<OverviewPageProps> = ({ shell, overview }) => {
  const { manualVersionId, manualTitle } = shell
  const { reading, resume, review, quiz, mastery, tutorRecent, chapters } = overview

  const [draft, setDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleAskTutor = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const text = draft.trim()
      if (text.length === 0 || submitting) return

      setSubmitting(true)
      createTutorConversation(manualVersionId)
        .then((conversation) => {
          const url = `${tutorConversationUrl(manualVersionId, conversation.id)}?draft=${encodeURIComponent(text)}`
          router.visit(url)
        })
        .catch(() => {
          setSubmitting(false)
        })
    },
    [draft, submitting, manualVersionId],
  )

  const readingRatio = ratioOf(reading.completedCount, reading.totalCount)
  const masteryTotalChapters = mastery.solidCount + mastery.weakCount + mastery.untestedCount
  const masteryRatio = ratioOf(mastery.solidCount, masteryTotalChapters)
  const lastSessionRatio =
    quiz.lastSession != null && quiz.lastSession.score != null
      ? ratioOf(quiz.lastSession.score, quiz.lastSession.total)
      : 0

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden" data-testid="overview-page">
      <div
        className="flex-1 overflow-y-auto"
        style={{
          padding: 'var(--space-6) var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-6)',
        }}
      >
        {/* Saluto narrativo */}
        <div>
          {/* sectionCompact as h1 — the shared top-level heading of every
              course section (see ReaderDocument/TestsHub/MasteryPanel). */}
          <Heading variant="sectionCompact" as="h1">
            {greetingPrefix(new Date().getHours())}
          </Heading>
          <Text variant="lead" style={{ marginTop: 'var(--space-2)', display: 'block' }}>
            {resume != null
              ? `L'ultima volta hai chiuso «${resume.sectionTitle}».`
              : `${manualTitle} ti aspetta: pronto a iniziare?`}
          </Text>
        </div>

        {/* Polso — Lettura / Padronanza / Verifica */}
        <div
          style={{
            display: 'flex',
            border: '1px solid var(--line)',
            borderRadius: 12,
            overflow: 'hidden',
            background: 'var(--surface)',
          }}
        >
          <PulseItem
            testId="overview-pulse-lettura"
            label="Lettura"
            ratio={readingRatio}
            primary={
              reading.totalCount === 0
                ? 'Il manuale non ha ancora sezioni'
                : `Hai letto ${reading.completedCount} sezioni su ${reading.totalCount}`
            }
            secondary={
              reading.reviewCount > 0
                ? `${reading.reviewCount} da rivedere`
                : 'Nessuna sezione da rivedere'
            }
          />
          <PulseItem
            testId="overview-pulse-padronanza"
            label="Padronanza"
            ratio={masteryRatio}
            primary={
              mastery.totalQuestions === 0
                ? 'Ancora nessun quiz per misurare la padronanza'
                : masteryTotalChapters === 0
                  ? 'Servono più risposte per un quadro affidabile'
                  : `${mastery.solidCount} capitoli solidi su ${masteryTotalChapters}`
            }
            secondary={
              mastery.weakCount > 0
                ? `${mastery.weakCount} da rinforzare`
                : mastery.untestedCount > 0
                  ? `${mastery.untestedCount} ancora da testare`
                  : 'Tutto sotto controllo'
            }
          />
          <PulseItem
            testId="overview-pulse-verifica"
            label="Verifica"
            ratio={lastSessionRatio}
            last
            primary={
              quiz.sessionsCompleted === 0
                ? 'Non hai ancora completato un quiz'
                : `Hai completato ${quiz.sessionsCompleted} quiz`
            }
            secondary={
              quiz.lastSession != null
                ? `Ultimo: ${quiz.lastSession.score ?? '—'}/${quiz.lastSession.total} il ${formatDate(quiz.lastSession.completedAt)}`
                : 'Fai il primo quiz per iniziare'
            }
          />
        </div>

        {/* Continua da qui + Da rivedere */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card padding="lg" data-testid="overview-resume-card">
            <Heading variant="eyebrow" className="text-accent">
              Continua da qui
            </Heading>
            {resume != null ? (
              <>
                <Heading variant="cardXl" style={{ marginTop: 'var(--space-2)' }}>
                  {resume.sectionTitle}
                </Heading>
                <Text variant="meta">{resume.chapterTitle}</Text>
                <div style={{ marginTop: 'var(--space-4)' }}>
                  <Button
                    as={Link}
                    href={sectionUrl(manualVersionId, resume.slug)}
                    variant="accent"
                    size="md"
                  >
                    Riprendi
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Heading variant="cardXl" style={{ marginTop: 'var(--space-2)' }}>
                  Non hai ancora iniziato
                </Heading>
                <Text variant="body" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
                  Parti dal principio: il manuale ti aspetta.
                </Text>
                <div style={{ marginTop: 'var(--space-4)' }}>
                  <Button
                    as={Link}
                    href={letturaEntryUrl(manualVersionId)}
                    variant="accent"
                    size="md"
                  >
                    Inizia a leggere
                  </Button>
                </div>
              </>
            )}
          </Card>

          <Card padding="lg" data-testid="overview-review-card">
            <Heading variant="eyebrow">Da rivedere</Heading>
            {review.length > 0 ? (
              <ul
                style={{
                  marginTop: 'var(--space-3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                  listStyle: 'none',
                  padding: 0,
                }}
              >
                {review.map((item) => (
                  <li key={item.slug}>
                    <Link
                      href={sectionUrl(manualVersionId, item.slug)}
                      style={{
                        color: 'var(--ink)',
                        textDecoration: 'none',
                        fontFamily: 'var(--font-sans)',
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {item.title}
                    </Link>
                    <Text variant="meta" style={{ display: 'block' }}>
                      {item.chapterTitle}
                    </Text>
                  </li>
                ))}
              </ul>
            ) : (
              <Text variant="body" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
                Nessuna sezione segnata da rivedere. Ottimo lavoro.
              </Text>
            )}
            <Link
              href={quizHubUrl(manualVersionId)}
              style={{
                display: 'inline-block',
                marginTop: 'var(--space-4)',
                color: 'var(--accent)',
                fontFamily: 'var(--font-sans)',
                fontSize: 13,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Pratica i punti deboli →
            </Link>
          </Card>
        </div>

        {/* Mappa del corso */}
        {chapters.length > 0 && (
          <div>
            <Heading variant="eyebrow">Mappa del corso</Heading>
            <div
              style={{
                marginTop: 'var(--space-3)',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 'var(--space-3)',
              }}
            >
              {chapters.map((chapter, index) => (
                <ChapterTile
                  key={chapter.id}
                  chapter={chapter}
                  index={index}
                  manualVersionId={manualVersionId}
                  isCurrent={resume != null && resume.chapterId === chapter.id}
                  resumeSlug={resume?.slug ?? null}
                />
              ))}
            </div>
          </div>
        )}

        {/* Riprendi col tutor */}
        <div>
          <Heading variant="eyebrow">Riprendi col tutor</Heading>
          {tutorRecent.length > 0 ? (
            <ul
              style={{
                marginTop: 'var(--space-3)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                listStyle: 'none',
                padding: 0,
              }}
            >
              {tutorRecent.map((conversation) => (
                <li key={conversation.id}>
                  <Link
                    href={tutorConversationUrl(manualVersionId, conversation.id)}
                    style={{
                      color: 'var(--ink)',
                      textDecoration: 'none',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    {conversation.title ?? 'Conversazione senza titolo'}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <Text variant="body" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
              Non hai ancora chiesto nulla al tutor. Scrivi qui sotto per iniziare.
            </Text>
          )}
        </div>
      </div>

      {/* Barra tutor — SOLO in Panoramica (decisione chiusa, vedi task doc) */}
      <form
        onSubmit={handleAskTutor}
        data-testid="overview-tutor-form"
        className="shrink-0"
        style={{
          borderTop: '1px solid var(--line)',
          padding: 'var(--space-4) var(--space-5)',
          display: 'flex',
          gap: 'var(--space-3)',
          background: 'var(--bg)',
        }}
      >
        <Input
          aria-label="Chiedi al tutor"
          placeholder="Chiedi al tutor…"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={submitting}
          style={{ flex: 1 }}
        />
        <Button type="submit" variant="accent" disabled={submitting || draft.trim().length === 0}>
          Chiedi
        </Button>
      </form>
    </div>
  )
}

Overview.layout = (page) => <CourseLayout>{page}</CourseLayout>

export default Overview
